import React, { useEffect, useMemo, useState } from "react";
import CustomButton from "../../common/custom-button/custom-button";
import { DeleteOutlined, EditOutlined, ExportOutlined, PlusOutlined, PrinterOutlined } from "@ant-design/icons";
import CreateExam from "./components/create-exam/create-exam";
import { useForm } from "react-hook-form";
import ExamScheduleBoard from "./components/exam-schedule-board/exam-schedule-board";
import dayjs from "dayjs";
import { LeftOutlined } from "@ant-design/icons";
import CustomTag from "../../common/custom-tag/custom-tag";
import AddEditExamModal from "./components/add-edit-exam-modal/add-edit-exam-modal";
import {
  createExam,
  deleteExamSchedule,
  getSingleExamInfo,
  sendExamTimeTable,
  updateExamination,
} from "../../services/examination";
import { Alert, Spin } from "antd";
import ExamListTable from "./components/exam-list-table/exam-list-table";
import styles from "./schedule-examination.module.css";
import CustomPopover from "../../common/custom-popover/custom-popover";
import { toast } from "react-toastify";
import { useAcademicYears } from "../../hooks/useAcademicYear";
import { fetchTerms } from "../../services/school_info";

const defaultExamContext = {
  exam_name: "",
  exam_date: "",
  exam_info: null,
};

const ScheduleExamination = () => {
  const form = useForm();
  const [tableRender, setTableRender] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showExamForm, setShowExamForm] = useState(false);
  const [examContext, setExamContext] = useState({ ...defaultExamContext });
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [sendEmailLoading, setSendEmailLoading] = useState(false);
  const {
    selectedYear,
    selectedYearName,
    loading: yearLoading
  } = useAcademicYears();
  const [termList, setTermList] = useState([]);
  const activeTerm = useMemo(() => {
    return termList?.find((t) => t?.is_active) || null;
  }, [termList]);

  useEffect(() => {
    fetchTermList();
  }, []);

  const fetchTermList = async () => {
    try {
      setLoading(true);
      const resp = await fetchTerms();
      setTermList(resp?.data?.data?.terms || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveExam = async () => {
    try {
      const formValues = form.getValues();
      const payload = {
        term_id: formValues.term_id,
        exam_type_id: formValues.exam_type,
        exam_name: formValues.exam_name,
        exam_start_date: dayjs(formValues.exam_start_date).format("YYYY-MM-DD"),
        exam_end_date: dayjs(formValues.exam_end_date).format("YYYY-MM-DD"),
        max_slot_per_day: formValues.max_slots,
      };
      setLoading(true);
      let resp = null;
      if (showCalendar) {
        resp = await updateExamination(examContext.exam_id, payload);
      } else resp = await createExam(payload);
      const examData = resp?.data?.data;
      setExamContext((pre) => ({
        ...pre,
        exam_name: examData?.exam_name ?? "Test Exam",
        exam_id: examData?.id,
      }));
      const examResp = await getSingleExamInfo({ exam_id: examData?.id });
      fetchAndRenderTable(examResp);
      setShowCalendar(true);
    } catch (err) {
      console.log(err);
      toast.error(err?.data?.message || "Failed to save exam");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExam = async (item) => {
    try {
      setLoading(true);
      await deleteExamSchedule(item?.id);
      const resp = await getSingleExamInfo({ exam_id: item?.exam_id });
      fetchAndRenderTable(resp);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const generateSchedulePdf = async (examId, examSchedules) => {
    const payload = {
      exam_id: examId,
      is_export: true
    };
    const response = await getSingleExamInfo(payload);
    const base64Url = response?.data?.data?.export_data?.pdf_base64;

    if (base64Url && examSchedules && Object.keys(examSchedules).length > 0) {
      const url = toPdfDataUrl(base64Url);
      setPdfUrl(url);
    } else {
      setPdfUrl(null);
    }
  };

  const fetchAndRenderTable = (examResp) => {
    try {
      const schedules = examResp?.data?.data?.set_exams;
      const examsInfo = examResp?.data?.data?.exam;
      generateSchedulePdf(examsInfo?.id, schedules);

      const slots = examsInfo?.max_slot_per_day;
      const startDate = dayjs(examsInfo?.exam_start_date, {
        format: "YYYY-MM-DD",
      });
      const endDate = dayjs(examsInfo?.exam_end_date, { format: "YYYY-MM-DD" });
      const dayDiff = dayjs(endDate).diff(dayjs(startDate), "days");

      const renderItem = Array.from({ length: slots }, (_, trIdx) => {
        return (
          <tr key={"x" + trIdx}>
            {Array.from({ length: dayDiff + 1 }).flatMap((_, tdIdx) => {
              const currDate = dayjs(startDate).add(tdIdx, "days");
              const currDayIdx = dayjs(currDate).toDate().getDay();
              const formattedCurrDate = currDate.format("YYYY-MM-DD");
              const item = schedules?.[formattedCurrDate]?.[trIdx];

              return !trIdx && [6, 0].includes(currDayIdx) ? (
                <td key={"x" + tdIdx} rowSpan={slots}></td>
              ) : trIdx && [6, 0].includes(currDayIdx) ? (
                []
              ) : (
                <td key={"y" + tdIdx}>
                  {!item ? (
                    <div className={styles["set-exam-btn-container"]}>
                      <CustomButton
                        title="Set Exam"
                        size="large"
                        style={{ height: 40 }}
                        onClick={() => {
                          setIsModalOpen(true);
                          const newDate = dayjs(startDate).add(tdIdx, "days");
                          setExamContext((pre) => ({
                            ...pre,
                            exam_date: newDate,
                            exam_info: null,
                            exam_id: examsInfo.id,
                          }));
                        }}
                      />
                    </div>
                  ) : (
                    <div className={styles["exam-slot-container"]}>
                      <div className={styles["exam-slot-box"]}>
                        <span>
                          {dayjs(item?.start_time, "HH:mm:ss").format(
                            "hh:mm a"
                          )}
                          &nbsp; -{" "}
                          {dayjs(item?.end_time, "HH:mm:ss").format("hh:mm a")}
                        </span>
                        <div>
                        <span
                          onClick={() => {
                            setIsModalOpen(true);
                            setExamContext((pre) => ({
                              ...pre,
                              exam_date: currDate,
                              exam_info: item,
                              exam_id: examsInfo?.id,
                            }));
                          }}
                          className={styles["exam-slot-edit"]}
                        >
                          <EditOutlined />
                        </span>
                        {dayjs(item?.examination_date).isAfter(dayjs(), "day") ? (
                          <CustomPopover
                            message="Are you sure you want to delete?"
                            confirmText="Delete"
                            cancelText="Cancel"
                            onConfirm={() => handleDeleteExam(item)}
                            style={{ marginLeft: 8 }}
                          >
                            <DeleteOutlined />
                          </CustomPopover>
                        ) : null}
                        </div>
                      </div>
                      <div className={styles["exam-slot-detail"]}>
                        Level: {item?.level?.level_name}
                      </div>
                      <div className={styles["exam-slot-subject"]}>
                        <span>Subject:&nbsp;</span>
                        <CustomTag
                          text={item?.subject_name}
                          color="#31AF42"
                          background="#29BC3D1A"
                          borderColor="#9BDBA3"
                        />
                      </div>
                      <div className={styles["exam-slot-sections"]}>
                        <span>Sections:&nbsp;</span>
                        <span>
                          {item?.sections_and_details?.map((i) => i?.section_name)?.join(", ")}
                        </span>
                      </div>
                    </div>
                  )}
                </td>
              );
            })}
          </tr>
        );
      });

      setTableRender(renderItem);
    } catch (err) {
      console.log(err);
    }
  };

  const onClickCreateForm = () => {
    setShowExamForm(true);
    form.reset({
      term_id: activeTerm?.id || null,
      exam_name: "",
      exam_type: null,
      exam_start_date: null,
      exam_end_date: null,
      max_slots: null,
    });
  };

  const dateChangerView = `${dayjs(form.getValues().exam_start_date).format(
    "MMM DD, YYYY"
  )} - ${dayjs(form.getValues().exam_end_date).format("MMM DD, YYYY")}`;

  const toPdfDataUrl = (b64) => {
    const trimmed = (b64 || '').trim();
    return /^data:application\/pdf;base64,/.test(trimmed)
      ? trimmed
      : `data:application/pdf;base64,${trimmed}`;
  };

  const onViewClick = async (record) => {
    try {
      setLoading(true);
      const examResp = await getSingleExamInfo({ exam_id: record?.id });
      fetchAndRenderTable(examResp);
      const info = examResp?.data?.data?.exam;
      setExamContext((pre) => ({
        ...pre,
        exam_name: record?.exam_name ?? "Test Exam",
        exam_id: info?.id,
      }));

      form.reset({
        term_id: info?.term_id,
        exam_name: info?.exam_name,
        exam_type: info?.exam_type_id,
        exam_start_date: dayjs(info?.exam_start_date, { format: "YYYY-MM-DD" }),
        exam_end_date: dayjs(info?.exam_end_date, { format: "YYYY-MM-DD" }),
        max_slots: info?.max_slot_per_day,
      });

      setShowCalendar(true);
      setShowExamForm(true);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const sendMail = async () => {
    try {
      setSendEmailLoading(true);
      const payload = { exam_id: examContext?.exam_id };
      await sendExamTimeTable(payload);
      toast.success("Exam schedule has been send successfully");
    } catch (err) {
      console.log(err);
    } finally {
      setSendEmailLoading(false);
    }
  };

  const handlePrintPdf = () => {
    try {
      const base64 = pdfUrl.split(",")[1];
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(blobUrl);
        }, 2000);
      };
    } catch (error) {
      console.error("Print error:", error);
    }
  };

  const handleDownloadPdf = () => {
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `examination-timetable.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <div className={styles["dashboard-header"]}>
        <div className={styles["header-title"]}>
          <h3>Exam management dashboard</h3>
          <p className={styles.yearText}>
            Academic Year: {selectedYearName}
          </p>
        </div>
        {showExamForm ? (
          <div className={styles.buttonContainer}>
            <CustomButton
              title="Send Email"
              onClick={sendMail}
              disabled={!pdfUrl}
              loading={sendEmailLoading}
            />
            <CustomButton
              title="Print"
              icon={<PrinterOutlined />}
              onClick={handlePrintPdf}
              disabled={!pdfUrl}
            />
            <CustomButton
              title="Export"
              icon={<ExportOutlined />}
              onClick={handleDownloadPdf}
              disabled={!pdfUrl}
            />
          </div>
        ) : (
          <CustomButton
            title="Schedule Examination"
            size="large"
            icon={<PlusOutlined />}
            onClick={onClickCreateForm}
          />
          )}
      </div>
      <Spin spinning={loading || yearLoading}>
        {showExamForm ? (
          <>
            <div
              onClick={() => {
                setShowExamForm(false);
                setShowCalendar(false);
              }}
              className={styles["back-to-list"]}
            >
              <LeftOutlined style={{ color: "#667085" }} />
              <div style={{ fontWeight: 500 }}>Examination List</div>
            </div>
            {!showCalendar && !activeTerm ? (
              <Alert
                type="error"
                showIcon
                message="No Active Term Found"
                description="There is currently no active term available. Please create or activate a term to continue."
              />
            ) : (
              <CreateExam 
                form={form} 
                handleSaveExam={handleSaveExam} 
                termList={termList}
              />
            )}
            {showCalendar ? (
              <div style={{ marginTop: 16 }}>
                <div className={styles["date-nav-container"]}>
                  <div className={styles["date-range-display"]}>
                    {dateChangerView}
                  </div>
                </div>
                <ExamScheduleBoard form={form}>{tableRender}</ExamScheduleBoard>
              </div>
            ) : null}
          </>
        ) : (
          <ExamListTable 
            onViewClick={onViewClick} 
            selectedYear={selectedYear}
          />
        )}
      </Spin>

      <AddEditExamModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(!isModalOpen)}
        examContext={examContext}
        fetchAndRenderTable={fetchAndRenderTable}
      />
    </div>
  );
};

export default ScheduleExamination;
